import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, Ticket, Puzzle, QrCode, Settings, LogOut, Menu, X, ExternalLink } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { ASSETS } from "../../data/assets";
import { cn } from "../../lib/utils";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/activities", label: "Activities", icon: Puzzle },
  { to: "/admin/tickets", label: "Tickets", icon: Ticket },
  { to: "/admin/check-in", label: "Check-In", icon: QrCode },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { admin, logout } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!admin) return <Navigate to="/admin" replace />;

  const SidebarInner = () => (
    <>
      <div className="px-5 py-5 border-b border-slate-100">
        <img src={ASSETS.logo} alt="BLTR 2026" className="h-11" />
        <p className="text-xs font-bold text-slate-400 mt-1">Admin Portal</p>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors",
                isActive ? "bg-sky/25 text-sea-deep" : "text-slate-500 hover:bg-slate-100"
              )
            }
          >
            <n.icon className="w-5 h-5" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-100 space-y-1">
        <button
          onClick={() => navigate("/")}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg font-semibold text-sm text-slate-500 hover:bg-slate-100"
        >
          <ExternalLink className="w-5 h-5" /> Lihat Website
        </button>
        <button
          onClick={() => { logout(); navigate("/admin"); }}
          data-testid="admin-logout"
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg font-semibold text-sm text-destructive hover:bg-destructive/10"
        >
          <LogOut className="w-5 h-5" /> Keluar
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 lg:grid lg:grid-cols-[260px_1fr]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col bg-white border-r border-slate-200 h-screen sticky top-0">
        <SidebarInner />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 flex items-center justify-between px-4 h-14">
        <img src={ASSETS.logo} alt="BLTR" className="h-9" />
        <button onClick={() => setOpen(true)} className="p-2 text-slate-600" aria-label="Menu" data-testid="admin-menu-open">
          <Menu className="w-6 h-6" />
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative bg-white w-64 flex flex-col h-full">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-3 p-1 text-slate-500" aria-label="Tutup"><X className="w-5 h-5" /></button>
            <SidebarInner />
          </aside>
        </div>
      )}

      <main className="p-4 sm:p-6 lg:p-8 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
